import type {
  PostV1SkillsCategoriesResponse,
  ProfileLinkConfig,
  SkillTag,
} from "../../client";
import { useGetSkillCategories } from "../../services";

/**
 * useAddStackSkill — adds a skill to the main skill stack
 * (skillsSummaryConfig.skillCategoryBlocks), categorising it into the right
 * block via POST /v1/skills/categories (same mapping the drawer uses). The
 * category block is created if it doesn't exist yet, so nothing is dropped.
 */

// subCategory → skillCategoryBlock title. Kept in sync with LinkCreatorSkills.
const LANGUAGES_AND_FRAMEWORKS = [
  "Frameworks & Libraries",
  "Mobile Development",
  "Programming Languages",
  "CSS Preprocessors",
  "CSS Frameworks",
  "JavaScript Frameworks",
  "Machine Learning",
  "Testing",
  "Frontend Development",
];
const TECHNOLOGIES = [
  "Software Architecture & System Design",
  "Cybersecurity & Compliance",
  "API Design",
  "Site Reliability Engineering",
  "Containerization & Virtualization",
  "Authentication & Authorization",
  "Infrastructure as Code",
  "API Management",
  "Cloud Platforms",
  "Protocols",
  "Networking",
  "Application Servers",
  "Build & Dependency Management",
  "Data Engineering",
  "Infrastructure & Operations",
  "Security & Quality Assurance",
  "Version Control & Collaboration",
  "Monitoring & Observability",
  "DevOps",
  "Machine Learning & AI",
  "Messaging & Event Streaming",
  "Operating Systems",
  "Architectural Patterns",
];
const DATABASES = [
  "Databases & Data Management",
  "Data Processing",
  "Database Management",
];
const TOOLS_AND_METHODS = [
  "Development Tools",
  "Productivity Tools",
  "UI/UX Design",
  "Business & Specialized Skills",
  "IDE & Development Tools",
  "Agile Methodologies",
];

const titleForResponse = (response: PostV1SkillsCategoriesResponse): string => {
  if (!response || response.length === 0) return "Other";
  const sub = response[0].subCategory;
  if (LANGUAGES_AND_FRAMEWORKS.includes(sub)) return "Languages and frameworks";
  if (TECHNOLOGIES.includes(sub)) return "Technologies";
  if (TOOLS_AND_METHODS.includes(sub)) return "Tools and methods";
  if (DATABASES.includes(sub)) return "Databases";
  return "Other";
};

export function useAddStackSkill(
  config: ProfileLinkConfig,
  onUpdate: (patch: Partial<ProfileLinkConfig>) => void,
) {
  const { mutate: getSkillCategory } = useGetSkillCategories();

  const writeToBlock = (tag: SkillTag, title: string) => {
    const blocks = config.skillsSummaryConfig?.skillCategoryBlocks ?? [];
    if (
      blocks.some((b) =>
        b.skillTags.some(
          (s) => s.name.toLowerCase() === tag.name.toLowerCase(),
        ),
      )
    ) {
      return;
    }
    let found = false;
    const updated = blocks.map((b) => {
      if (b.title !== title) return b;
      found = true;
      return { ...b, skillTags: [...b.skillTags, tag] };
    });
    if (!found) updated.push({ title, skillTags: [tag] });
    onUpdate({
      skillsSummaryConfig: {
        ...config.skillsSummaryConfig,
        skillCategoryBlocks: updated,
      },
    });
  };

  return (tag: SkillTag) => {
    if (!tag.name.trim()) return;
    getSkillCategory(
      { body: { skills: [tag.name] } },
      {
        onSuccess: (response) => writeToBlock(tag, titleForResponse(response)),
        onError: () => writeToBlock(tag, "Other"),
      },
    );
  };
}
